import OfferRouteGroupItem from '@components/pages/Products/components/RouteProduct/components/OfferRouteGroupItem';
import { getCargoDescription } from '@components/pages/Products/components/RouteProduct/helpres';
import { ProductComponentProps } from '@components/pages/Products/types';
import { getLocalizationById } from '@context/useMyRouting/helpers';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import Typography from '@mui/material/Typography';
import { getCargo } from '@pages/Basket/components/ServicesList/components/OrderList/components/OrderListItem/helpers';
import { getCartPriceSummary } from '@pages/Basket/helpers';
import OfferTipsText from '@pages/OfferDetail/components/ProductList/components/OfferTipsText';
import { getValidityCalcDate } from '@pages/OfferDetail/helpers';
import { RouteResult } from '@services/requests/orderService/types';
import React, { createContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import RouteSummary from 'src/components/pages/Products/components/RouteProduct/components/RouteSummary';

import { RouteProductContextData } from './types';

/*
Контекст с базовыми данными, которые грузятся динамически и не привязаны к продуктам
 */
export const RouteProductContext = createContext<RouteProductContextData | undefined>(undefined);

const RouteProduct = (props: ProductComponentProps) => {
  const { product, order, realTimeBaseData, taxNames, isUseActualCourses } = props;
  const langCode = getLocalizationById(order.language_id).countryCode;
  const { t } = useTranslation(['OfferDetail', 'CartSummary', 'RoutesListComponent'], {
    lng: langCode,
  });

  const summary = useMemo(() => {
    const orderOnlyThisProduct = {
      ...order,
      products: [product],
    };
    return getCartPriceSummary(orderOnlyThisProduct);
  }, [order]);

  const productData: RouteResult = useMemo(() => JSON.parse(product?.data || '{}'), [product]);

  const validDateText = useMemo(() => getValidityCalcDate([product], langCode), [order.products]);

  const currencyRoute = useMemo(
    () => productData.baseData?.currencies.find((c) => c.id === order.currency_id),
    [productData, order]
  );

  const { cargoParameters } = productData;

  // Получаем данные маршрута
  const route: RouteResult = JSON.parse(product.data);

  // Получаем описание типа груза
  const cargo = getCargo(
    route.type,
    {
      containers: realTimeBaseData.containers,
      units: realTimeBaseData.units,
      trucks: realTimeBaseData.trucks,
    },
    {
      containers: route.cargoParameters.containerParameters,
      customs: route.cargoParameters.customCargoParameters,
      trucks: route.cargoParameters.truckParameters,
    },
    langCode,
    order.language_id
  ).join('; ');

  if (!product || !realTimeBaseData || !currencyRoute) {
    return null;
  }

  const cargoParameterIds = useMemo(
    () => [
      ...(productData?.cargoParameters.customCargoParameters ?? []).map((r) => r.identifier),
      ...(productData?.cargoParameters.containerParameters ?? []).map((r) => r.identifier),
      ...(productData?.cargoParameters.truckParameters ?? []).map((r) => r.identifier),
    ],
    [productData?.cargoParameters]
  );

  const sortedRoutes = useMemo(
    () =>
      cargoParameterIds
        .map((cargoId) => (productData?.route?.routes ?? []).find((r) => r.cargoID === cargoId))
        .filter(Boolean),
    [cargoParameterIds, productData?.route?.routes]
  );

  const cargoArray = cargo.split(';');
  const notCompeleteRouteParams = route.route.notCompleteType ?? [];

  return (
    <RouteProductContext.Provider
      value={{
        currencyRoute,
        product,
        productData,
        realTimeBaseData,
        primaryLanguageId: '1',
        secondaryLanguageId: '2',
      }}
    >
      <RouteSummary
        {...summary}
        cargo={cargoArray}
        order={order}
        product={product}
        orderCurrency={order.currency_id}
        langId={order.language_id}
        products={[]}
        isUseActualCourses={isUseActualCourses}
        realTimeBaseData={realTimeBaseData}
      />
      <Typography variant={'subtitle2'} component={'div'} className="add-info">
        {t('OfferDetail:RatesValid')} {validDateText}
      </Typography>
      {route.isNotCompleteResult && (
        <div className="route-notes">
          <div className="route-is-not-full">
            <ErrorOutlineIcon fontSize={'small'} />
            <div>{t('RoutesListComponent:isRouteNotCompleteName')}</div>
            {route.route.notCompleteType !== null && (
              <div className="not-complete-param-list">
                <ul>
                  {notCompeleteRouteParams.map((item, index) => (
                    <li
                      key={'not-complete-route-param-' + index}
                      className="not-complete-param-item"
                    >
                      {t('RoutesListComponent:NotCompleteResult.' + item)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="route-is-not-full__text">
              {t('RoutesListComponent:isRouteNotComplete')}
            </div>
          </div>
        </div>
      )}
      {route.route.hasCrossDocking && (
        <div className="route-notes">
          <div className="route-has-cross-docking">
            <ErrorOutlineIcon fontSize={'small'} />
            <div>{t('RoutesListComponent:isWithCrossDocking')}</div>
          </div>
        </div>
      )}
      <div>
        <OfferTipsText order={order} />
      </div>
      {sortedRoutes.map((routeGroup, index) => {
        const containersInfo = getCargoDescription(
          realTimeBaseData,
          route,
          routeGroup,
          langCode,
          order.language_id
        );

        return (
          <OfferRouteGroupItem
            order={order}
            cargo={containersInfo}
            key={`route-group-${routeGroup.routeId}`}
            routes={sortedRoutes}
            route={routeGroup}
            taxNames={taxNames}
            cargoType={route.type}
            containerParameters={cargoParameters.containerParameters || []}
            customCargoParameters={cargoParameters.customCargoParameters || []}
            truckParameters={cargoParameters.truckParameters || []}
            realTimeBaseData={realTimeBaseData}
            isUseActualCourses={isUseActualCourses}
            routeIndex={index}
          />
        );
      })}
    </RouteProductContext.Provider>
  );
};

export default RouteProduct;
